import { useState, type FormEvent } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import { useTrackedResidents } from '@/shared/hooks/useTracked';
import {
  useAccessCodes, createAccessCode, revokeAccessCode, purgeLegacyCodes,
  type AccessCodeRole, type AccessCode,
} from '@/shared/firebase/codes';
import { saveResident, generateResidentUid } from '@/shared/firebase/admin';
import { Modal } from '@/shared/components/Modal';
import { timeAgo } from '@/shared/utils/time';

export function AccessCodesTab() {
  const { codes, loading } = useAccessCodes();
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);

  function copy(code: string) {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 1200);
    });
  }

  async function purge() {
    if (!confirm('¿Borrar definitivamente códigos antiguos (rol "familiar") y revocados?')) return;
    setPurging(true);
    try {
      const { removed } = await purgeLegacyCodes();
      alert(`Se han borrado ${removed} código(s) obsoleto(s).`);
    } catch (e) {
      alert('Error: ' + (e instanceof Error ? e.message : String(e)));
    } finally { setPurging(false); }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Códigos de acceso ({codes.length})</h2>
          <p className="text-sm text-slate-500">
            Genera un código y entrégaselo a un cuidador o a un usuario. Con él entran sin email/contraseña.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={purge} disabled={purging} className="btn-ghost text-xs">
            {purging ? 'Limpiando…' : 'Limpiar legacy'}
          </button>
          <button onClick={() => setCreating(true)} className="btn-primary">+ Generar código</button>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-500">Cargando…</p>
      ) : codes.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-slate-500">No has generado códigos todavía.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {codes.map((c) => (
            <li key={c.code} className="card flex items-center gap-3">
              <RoleIcon role={c.role} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copy(c.code)}
                    className="font-mono text-xl font-extrabold text-brand hover:bg-brand-50 px-2 py-0.5 rounded transition"
                    title="Click para copiar"
                  >{c.code}</button>
                  {copied === c.code && <span className="text-xs text-success">¡Copiado!</span>}
                </div>
                <p className="text-sm text-slate-700 truncate">{c.label}</p>
                <p className="text-xs text-slate-500 truncate">
                  {c.role}
                  {c.residentUid && <> · vinculado a {c.residentUid}</>}
                  {' '}· creado {timeAgo(c.createdAt)}
                </p>
              </div>
              <button
                onClick={async () => {
                  if (confirm(`¿Revocar el código ${c.code}? Quien lo tenga dejará de poder acceder.`)) {
                    await revokeAccessCode(c.code);
                  }
                }}
                className="btn-danger text-xs"
              >Revocar</button>
            </li>
          ))}
        </ul>
      )}

      {creating && <CreateCodeModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function RoleIcon({ role }: { role: AccessCodeRole }) {
  const map: Record<AccessCodeRole, { tone: string; emoji: string }> = {
    cuidador: { tone: 'bg-brand-50 text-brand',     emoji: '👩‍⚕️' },
    usuario:  { tone: 'bg-warning-50 text-warning', emoji: '🆘' },
  };
  const m = map[role];
  return <div className={`w-12 h-12 rounded-xl grid place-items-center text-xl ${m.tone}`}>{m.emoji}</div>;
}

function CreateCodeModal({ onClose }: { onClose: () => void }) {
  const { user, centerId } = useAuth();
  const { residents } = useTrackedResidents(centerId);

  const [role, setRole] = useState<AccessCodeRole>('cuidador');
  const [label, setLabel] = useState('');
  const [length, setLength] = useState(7);
  const [residentMode, setResidentMode] = useState<'existing' | 'new'>('existing');
  const [residentUid, setResidentUid] = useState('');
  const [newResidentName, setNewResidentName] = useState('');

  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<AccessCode | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      if (!label.trim()) throw new Error('Pon una etiqueta para reconocer este código');

      let effectiveResidentUid = residentUid;

      if (role === 'usuario') {
        if (residentMode === 'new') {
          if (!newResidentName.trim()) throw new Error('Pon un nombre al usuario');
          effectiveResidentUid = generateResidentUid();
          await saveResident(centerId, effectiveResidentUid, { name: newResidentName.trim() });
        } else {
          if (!residentUid) throw new Error('Selecciona el usuario');
        }
      }

      const c = await createAccessCode({
        role, centerId, label: label.trim(), length,
        residentUid: role === 'usuario' ? effectiveResidentUid : undefined,
        createdBy: user?.email || undefined,
      });
      setCreated(c);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  }

  if (created) {
    return (
      <Modal onClose={onClose}>
        <div className="text-center py-2">
          <p className="text-sm text-slate-500 mb-1">Código generado</p>
          <p className="font-mono text-4xl font-extrabold text-brand tracking-wider mb-3">{created.code}</p>
          <p className="text-sm text-slate-700">{created.label}</p>
          <p className="text-xs text-slate-500 mt-1 capitalize">{created.role}</p>
          <div className="flex gap-2 mt-5">
            <button onClick={() => navigator.clipboard.writeText(created.code)} className="btn-ghost flex-1">Copiar</button>
            <button onClick={onClose} className="btn-primary flex-1">Hecho</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-bold mb-4">Generar código de acceso</h3>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Rol</label>
          <div className="grid grid-cols-2 gap-2">
            {(['cuidador', 'usuario'] as AccessCodeRole[]).map((r) => (
              <button
                key={r} type="button"
                onClick={() => { setRole(r); setResidentUid(''); }}
                className={`rounded-xl border py-2 text-sm font-semibold transition capitalize
                  ${role===r ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              >{r}</button>
            ))}
          </div>
        </div>

        {role === 'usuario' && (
          <div>
            <label className="label">Usuario al que pertenece</label>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setResidentMode('existing')}
                className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition
                  ${residentMode==='existing' ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              >Existente</button>
              <button
                type="button"
                onClick={() => setResidentMode('new')}
                className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition
                  ${residentMode==='new' ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              >Crear nuevo</button>
            </div>
            {residentMode === 'existing' ? (
              residents.length === 0 ? (
                <p className="text-sm text-slate-500 p-3 border border-slate-200 rounded-xl">
                  No hay usuarios registrados. Cambia a "Crear nuevo".
                </p>
              ) : (
                <select className="input" value={residentUid} onChange={(e) => setResidentUid(e.target.value)} required>
                  <option value="">— Selecciona —</option>
                  {residents.map((r) => <option key={r.uid} value={r.uid}>{r.config?.name || r.uid}</option>)}
                </select>
              )
            ) : (
              <input
                className="input" value={newResidentName}
                onChange={(e) => setNewResidentName(e.target.value)}
                placeholder="Nombre del usuario"
                required
              />
            )}
          </div>
        )}

        <div>
          <label className="label">Etiqueta</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ej: Carmen, hija de María García" required />
        </div>

        <div>
          <label className="label flex items-center justify-between">
            <span>Longitud del código</span>
            <span className="text-brand font-bold">{length} caracteres</span>
          </label>
          <input type="range" min={6} max={8} value={length} onChange={(e) => setLength(Number(e.target.value))} className="w-full accent-brand" />
        </div>

        {err && <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{err}</div>}

        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving} className="btn-primary flex-1">
            {saving ? 'Generando…' : 'Generar'}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}
